import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';

import { ButtonTitleWithLoading } from '@vyce/core/src/components';
import {
  getFormFoldersRequest,
  getFormTemplateFoldersRequest,
} from '@vyce/core/src/api/forms';
import { FormFolderDoc, FormTemplateFolderDoc } from '@vyce/core/src/api/types';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { FolderAutocomplete } from './FolderAutocomplete';

const FORM_FOLDERS_FETCH_LIMIT = 500;

interface Props {
  open: boolean;
  loading?: boolean;
  companyId: string;
  userId: string;
  kind?: 'form' | 'template';
  onClose: () => void;
  onConfirm: (folderId: string) => void | Promise<void>;
}

export const ChangeFolderDialog: React.FC<Props> = ({
  open,
  loading = false,
  companyId,
  userId,
  kind = 'form',
  onClose,
  onConfirm,
}) => {
  const { handleServerError } = useContext(NotificationContext);
  const [levels, setLevels] = useState<FormFolderDoc[][]>([]);
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);

  const selectedFolderId = selectedPath[selectedPath.length - 1] || '';

  const loadFoldersByParent = useCallback(
    async (parentId: string | null): Promise<FormFolderDoc[]> => {
      if (kind === 'template') {
        const { folders } = await getFormTemplateFoldersRequest({
          companyId,
          userId,
          parentId,
          limit: FORM_FOLDERS_FETCH_LIMIT,
        });
        return folders as unknown as FormFolderDoc[];
      }
      const { folders } = await getFormFoldersRequest({
        companyId,
        userId,
        parentId,
        limit: FORM_FOLDERS_FETCH_LIMIT,
      });
      return folders;
    },
    [companyId, userId, kind]
  );

  useEffect(() => {
    if (!open) return;

    const loadRootFolders = async () => {
      try {
        setLoadingFolders(true);
        const rootFolders = await loadFoldersByParent(null);
        setLevels([rootFolders]);
        setSelectedPath([]);
      } catch (error) {
        handleServerError(error);
        setLevels([[]]);
        setSelectedPath([]);
      } finally {
        setLoadingFolders(false);
      }
    };

    void loadRootFolders();
  }, [handleServerError, loadFoldersByParent, open]);

  const handleChangeLevel = async (levelIndex: number, folderId: string) => {
    const nextPath = [...selectedPath.slice(0, levelIndex), folderId];
    setSelectedPath(nextPath);
    setLevels(prev => prev.slice(0, levelIndex + 1));

    if (!folderId) return;

    try {
      setLoadingFolders(true);
      const subfolders = await loadFoldersByParent(folderId);
      if (subfolders.length) {
        setLevels(prev => {
          const nextLevels = prev.slice(0, levelIndex + 1);
          nextLevels.push(subfolders);
          return nextLevels;
        });
      }
    } catch (error) {
      handleServerError(error);
    } finally {
      setLoadingFolders(false);
    }
  };

  const levelLabels = useMemo(
    () =>
      levels.map((_, index) => {
        if (index === 0) return 'Folder';
        if (index === 1) return 'Subfolder';
        return `Subfolder ${index}`;
      }),
    [levels]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change folder</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gridGap={8}>
          {levels.map((foldersAtLevel, levelIndex) => (
            <FolderAutocomplete
              key={`folder-level-${levelIndex}`}
              folders={foldersAtLevel}
              selectedFolderId={selectedPath[levelIndex] || ''}
              onFolderChange={folderId => {
                void handleChangeLevel(levelIndex, folderId);
              }}
              loading={loadingFolders}
              label={levelLabels[levelIndex]}
            />
          ))}
          {!loadingFolders && levels[0]?.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              No available folders
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button size="large" variant="outlined" onClick={onClose} disabled={loading || loadingFolders}>
          Cancel
        </Button>
        <Button
          size="large"
          style={{ minWidth: 115 }}
          color="primary"
          onClick={() => onConfirm(selectedFolderId)}
          variant="contained"
          disabled={!selectedFolderId || loading || loadingFolders}
        >
          <ButtonTitleWithLoading title="Change" loaderVariant="paper" loading={!!loading} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
